import { computed, reactive, ref } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { useSentry } from '../composables/sentry'
import { useTypesenseSearchQueryUrl } from '../composables/typesenseSearchQueryUrl'
import { FlowState } from '../constants/flowState'
import BaseModelsService from '../services/BaseModelsService'
import { buildFilterString } from '../utils/typesenseBuildFilterString'

interface CurrentFacets {
  [key: string]: Set<string>
}

const { handleError } = useSentry()

export const useBaseModelsStore = defineStore('BaseModelsStore', () => {
  const flow = ref<FlowState>(FlowState.Idle)

  const currentFacets = ref<CurrentFacets>({})
  // Used to create flat level selected filters area in Filtering UI
  const selectedFilters = computed(() => {
    const arr: any[] = []
    const keys = Object.keys(currentFacets.value)
    if (!keys?.length)
      return arr
    keys.forEach((key: string) => {
      currentFacets.value[key].forEach((value: string) => arr.push({ key, value }))
    })
    return arr
  })

  const searchParameters = reactive({
    exactMatch: false,
    loadingMore: false,
    page: 1,
    query: '',
    filterString: buildFilterString(currentFacets.value)
  })

  // Debounce search requests to Typesense
  // For clearing type functions call _getTypesenseResults directly - @bmrankin
  const getTypesenseResults = useDebounceFn(async (payload: any = {}) => {
    await _getTypesenseResults(payload)
  }, 275)

  function clearCurrentFacets() {
    currentFacets.value = {}
    searchParameters.filterString = buildFilterString(currentFacets.value)
    _getTypesenseResults()
  }

  function updateCurrentFacets(payload: any) {
    const {
      key,
      value
    } = payload
    // Doesn't exist the create facet group and add value
    if (!currentFacets.value[key]) {
      currentFacets.value[key] = new Set([value])
    }
    // if the group exists and the value exists then remove
    else if (currentFacets.value[key].has(value)) {
      currentFacets.value[key].delete(value)
      if (!currentFacets.value[key].size)
        delete currentFacets.value[key]
    }
    // if the group exists but not the value then add
    else {
      currentFacets.value[key].add(value)
    }
    searchParameters.filterString = buildFilterString(currentFacets.value)
    getTypesenseResults()
  }

  function clearSearchQuery() {
    searchParameters.query = ''
    _getTypesenseResults()
  }

  const tsResults = ref({
    hits: [] as any[],
    facet_counts: {} as any,
    found: 0,
    page: 0
  })

  const tsResultsFacets = ref({
    facet_counts: {} as any
  })

  const hits = computed(() => tsResults.value.hits || [])

  async function _getTypesenseResults(payload: any = {}) {
    const {
      loadMore = false
    } = payload
    try {
      if (loadMore) {
        flow.value = FlowState.LoadingMore
        searchParameters.page++
      }
      else {
        flow.value = FlowState.Loading
        searchParameters.page = 1
      }

      const res = await BaseModelsService.getTypesenseResults(searchParameters)
      if (!res?.success)
        throw new Error('Trouble getting base models')

      if (loadMore) {
        tsResults.value.hits = tsResults.value.hits.concat(res.body?.results.hits)
        // tsResultsFacets.value = { ...res.body.facets }
      }
      else {
        tsResults.value = { ...res.body?.results }
        tsResultsFacets.value = { ...res.body?.facets }
      }

      flow.value = FlowState.Idle
    }
    catch (error: any) {
      handleError(error)
      flow.value = FlowState.Error
    }
  }

  const { searchQueryBuildFromUrl, searchQueryUrl } = useTypesenseSearchQueryUrl({
    searchParameters,
    currentFacets,
    updateCurrentFacets
  })

  return {
    hits,
    clearCurrentFacets,
    clearSearchQuery,
    currentFacets,
    flow,
    getTypesenseResults,
    searchParameters,
    searchQueryBuildFromUrl,
    searchQueryUrl,
    selectedFilters,
    tsResults,
    tsResultsFacets,
    updateCurrentFacets
  }
})
